import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import api from "../api/client";
import { getInviteInfo } from "../api/users";
import { useLangPrefix } from "../hooks/useLangPrefix";
import { formatApiError } from "../lib/apiErrorMessage";
import LanguageSwitcher from "../components/layout/LanguageSwitcher";
import PasswordInput from "../components/common/PasswordInput";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export default function SignupPage() {
  const { t } = useTranslation();
  const { prefix } = useLangPrefix();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get("callback_url") || "";
  const directInviteToken = searchParams.get("invite_token")?.trim() || "";
  const safeCallbackUrl = useMemo(() => {
    if (!callbackUrl) return "";
    if (callbackUrl.startsWith("/") && !callbackUrl.startsWith("//")) {
      return callbackUrl;
    }
    return "";
  }, [callbackUrl]);
  const loginHref = safeCallbackUrl
    ? `${prefix}/login?callback_url=${encodeURIComponent(safeCallbackUrl)}`
    : `${prefix}/login`;
  const inviteToken = useMemo(() => {
    if (directInviteToken) {
      return directInviteToken;
    }
    if (!safeCallbackUrl) return "";
    try {
      const parsed = new URL(safeCallbackUrl, window.location.origin);
      if (!/\/accept-invite\/?$/.test(parsed.pathname)) {
        return "";
      }
      return parsed.searchParams.get("invite_token")?.trim() || "";
    } catch {
      return "";
    }
  }, [directInviteToken, safeCallbackUrl]);
  const normalizedInputEmail = useMemo(() => normalizeEmail(email), [email]);
  const normalizedInvitedEmail = useMemo(() => normalizeEmail(invitedEmail), [invitedEmail]);
  const showInviteEmailMismatchWarning =
    Boolean(normalizedInvitedEmail) &&
    Boolean(normalizedInputEmail) &&
    normalizedInvitedEmail !== normalizedInputEmail;

  useEffect(() => {
    let active = true;
    if (!inviteToken) {
      setInvitedEmail("");
      return () => {
        active = false;
      };
    }

    const run = async () => {
      try {
        const info = await getInviteInfo(inviteToken);
        if (!active) return;
        setInvitedEmail(String(info?.email || "").trim());
      } catch (err) {
        if (!active) return;
        const status = err?.response?.status
        if (!status || (status !== 404 && status !== 410)) {
          Sentry.captureException(err, {
            tags: { module: 'auth', action: 'get_invite_info_signup' },
          })
        }
        setInvitedEmail("");
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t("signup.passwordsMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/manager/auth/send-otp", { email: email.trim() });
      const verifyPath = safeCallbackUrl
        ? `${prefix}/verify-otp?callback_url=${encodeURIComponent(safeCallbackUrl)}`
        : `${prefix}/verify-otp`;
      navigate(verifyPath, {
        state: {
          email: email.trim(),
          password,
          name: fullName.trim(),
          callbackUrl: safeCallbackUrl || null,
        },
      });
    } catch (err) {
      setError(formatApiError(err, t("common.requestFailed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-100 p-4 text-zinc-900 scheme-light">
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <LanguageSwitcher />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-100/70 via-transparent to-cyan-100/50" />

      <div className="w-full max-w-sm relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center">
            <img
              src="/full_logo.png"
              alt="Growchat"
              className="w-40 h-auto rounded-md object-cover"
            />
          </div>
          <p className="text-sm text-gray-500">{t("login.tagline")}</p>
        </div>

        <div className="card p-6">
          <h2 className="mb-5 text-lg font-semibold text-zinc-900">
            {t("signup.title")}
          </h2>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                {t("signup.fullName")}
              </label>
              <input
                type="text"
                className="input"
                placeholder={t("signup.fullNamePlaceholder")}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                {t("signup.email")}
              </label>
              <input
                type="email"
                className="input"
                placeholder={t("signup.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {showInviteEmailMismatchWarning && (
                <p className="mt-1.5 text-xs text-amber-700">
                  {t("signup.inviteEmailMismatchWarning", { email: invitedEmail })}
                </p>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                {t("signup.password")}
              </label>
              <PasswordInput
                className="input"
                placeholder={t("signup.passwordPlaceholder")}
                value={password}
                minLength={8}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                {t("signup.confirmPassword")}
              </label>
              <PasswordInput
                className="input"
                placeholder={t("signup.passwordPlaceholder")}
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center py-2.5 mt-1 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Spinner /> {t("signup.submitting")}
                </span>
              ) : (
                t("signup.submit")
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-5">
            {t("signup.hasAccount")}{" "}
            <Link
              to={loginHref}
              className="font-medium text-indigo-600 hover:text-indigo-700"
            >
              {t("signup.signIn")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" />;
}
